/**
 * Talking to a technician out loud.
 *
 * Both halves are the browser's own Web Speech API, and that is the whole
 * point: no server, no key, no per-use cost, nothing to deploy, and the
 * synthesis half works with the network down. It is also the half of this
 * feature a classroom can actually use on thirty machines at once.
 *
 * WHAT IT COSTS INSTEAD. Recognition is Chrome and Edge only (Safari has it
 * under a prefix and Firefox does not ship it), it needs one microphone
 * permission per origin, and on Chrome it goes to Google's servers to
 * transcribe, so it is not offline and it is not private the way the rest
 * of this app is. Synthesis is everywhere but the stock voices are flat.
 * Both facts are surfaced rather than hidden: the mic button is absent
 * where recognition is missing, and the panel says where dictation goes
 * the first time it is armed.
 *
 * A hosted voice later replaces the two `speak`/`listen` bodies here and
 * nothing else - the button, the state and the talking clip are the same
 * wiring either way.
 */

import { currentLanguage } from '../i18n/i18n'

/**
 * The BCP-47 tag each catalogue language speaks and listens in. i18next's
 * own codes (`zh_cn`) are not what the speech engines take.
 */
const SPEECH_TAG: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  zh_cn: 'zh-CN',
  zh_tw: 'zh-TW',
}

const speechTag = (): string => SPEECH_TAG[currentLanguage()] ?? 'en-US'

/*
 * The constructor is `webkitSpeechRecognition` on Chrome and Edge and
 * `SpeechRecognition` where it is unprefixed. TypeScript's DOM lib declares
 * the RESULT interfaces (SpeechRecognitionResultList and below) but neither
 * the constructor nor the event, so those two are spelled out here rather
 * than the whole thing being cast to any - the result list, which is what
 * this file actually reads, is then type-checked by the DOM lib.
 */
interface RecognitionResultEvent extends Event {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface RecognitionLike extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((e: RecognitionResultEvent) => void) | null
  onerror: ((e: Event & { error?: string }) => void) | null
  onend: (() => void) | null
}
type RecognitionCtor = new () => RecognitionLike

const recognitionCtor = (): RecognitionCtor | null => {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor
    webkitSpeechRecognition?: RecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Whether this browser can take dictation at all. */
export const canListen = (): boolean => recognitionCtor() !== null

/** Whether this browser can read a reply aloud. */
export const canSpeak = (): boolean => typeof window !== 'undefined' && 'speechSynthesis' in window

export interface ListenHandle {
  /** Stop listening and keep whatever was heard. */
  stop(): void
  /** Stop listening and discard it. */
  abort(): void
}

export interface ListenCallbacks {
  /** Fired as the words firm up, for the text box to show them. */
  interim(text: string): void
  /** The finished sentence. Fired once, at the end of the utterance. */
  final(text: string): void
  /** Recognition stopped, for any reason - including a `stop()` above. */
  end(reason: 'done' | 'error' | 'denied'): void
}

/**
 * Take one utterance from the microphone.
 *
 * Single-shot rather than continuous: a student says a question, it lands
 * in the box, they send it. Continuous listening would need a wake word or
 * a hold-to-talk, and both are worse in a classroom with thirty microphones
 * in one room.
 */
export function listen(cb: ListenCallbacks): ListenHandle | null {
  const Ctor = recognitionCtor()
  if (!Ctor) return null
  const rec = new Ctor()
  rec.lang = speechTag()
  rec.continuous = false
  rec.interimResults = true
  rec.maxAlternatives = 1
  let settled = false

  rec.onresult = (e: RecognitionResultEvent) => {
    let interim = ''
    let final = ''
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const result = e.results[i]
      const text = result[0]?.transcript ?? ''
      if (result.isFinal) final += text
      else interim += text
    }
    if (interim) cb.interim(interim.trim())
    if (final.trim()) {
      settled = true
      cb.final(final.trim())
    }
  }
  rec.onerror = (e) => {
    settled = true
    // `not-allowed` and `service-not-allowed` are the permission refusals;
    // everything else (no-speech, network, aborted) is an ordinary stop.
    cb.end(e.error === 'not-allowed' || e.error === 'service-not-allowed' ? 'denied' : 'error')
  }
  rec.onend = () => {
    cb.end(settled ? 'done' : 'error')
  }
  try {
    rec.start()
  } catch {
    // Called twice, or the tab lost the microphone: treated as a stop.
    return null
  }
  return {
    stop: () => rec.stop(),
    abort: () => {
      settled = true
      rec.abort()
    },
  }
}

/**
 * The voice each technician speaks in.
 *
 * The browser's list is whatever the machine has installed, so this picks
 * rather than requires: the right language first, then a voice whose name
 * suggests the right gender, then anything in that language. A machine
 * with one voice gets that voice for everyone, which is honest - the
 * alternative is refusing to speak.
 *
 * Voices load asynchronously on Chrome; `voiceschanged` is why this is
 * resolved per utterance instead of cached at module load.
 */
const FEMININE = /female|woman|zira|samantha|karen|moira|tessa|paulina|mónica|monica|huihui|hanhan|yating|mei-jia|meijia/i
const MASCULINE = /male|man|david|mark|daniel|alex|fred|jorge|diego|kangkang|yunyang|zhiwei/i

function pickVoice(body: 'f' | 'm'): SpeechSynthesisVoice | null {
  const tag = speechTag()
  const all = window.speechSynthesis.getVoices()
  if (!all.length) return null
  const lang = tag.split('-')[0]
  const inLanguage = all.filter((v) => v.lang.replace('_', '-').toLowerCase().startsWith(lang))
  if (!inLanguage.length) return null
  const exact = inLanguage.filter((v) => v.lang.replace('_', '-').toLowerCase() === tag.toLowerCase())
  const pool = exact.length ? exact : inLanguage
  const wanted = body === 'f' ? FEMININE : MASCULINE
  return pool.find((v) => wanted.test(v.name)) ?? pool[0]
}

/**
 * Read a technician's line aloud. `onEnd` fires when the voice stops for
 * any reason, so the talking clip and the panel's speaking state can be
 * driven by the actual audio rather than by an estimate of its length.
 */
export function speak(text: string, body: 'f' | 'm', onEnd: () => void): void {
  if (!canSpeak()) {
    onEnd()
    return
  }
  const synth = window.speechSynthesis
  synth.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = speechTag()
  // A shade under the default: fab-floor speech, not an announcement.
  utterance.rate = 1.0
  utterance.pitch = body === 'f' ? 1.1 : 0.95
  const apply = (): void => {
    const voice = pickVoice(body)
    if (voice) utterance.voice = voice
    utterance.onend = onEnd
    utterance.onerror = onEnd
    synth.speak(utterance)
  }
  // Chrome fills the voice list asynchronously; if it is empty, wait one
  // event for it rather than speaking in the default voice.
  if (synth.getVoices().length === 0) {
    const once = (): void => {
      synth.removeEventListener('voiceschanged', once)
      apply()
    }
    synth.addEventListener('voiceschanged', once)
    // ...but never wait forever for an event that may not come.
    setTimeout(() => {
      synth.removeEventListener('voiceschanged', once)
      if (!synth.speaking && !synth.pending) apply()
    }, 300)
  } else {
    apply()
  }
}

/** Stop mid-sentence: the student closed the panel or asked something else. */
export function stopSpeaking(): void {
  if (canSpeak()) window.speechSynthesis.cancel()
}
